import { PerspectiveBackboneModel, PerspectiveFilters } from 'aqTypes';
import {
  getModifiersAsQueryString,
  getPerspectiveModifiersAsQueryString,
} from 'perspectives/util';
import { ArdoqId, GroupingRule } from '@ardoq/api-types';
import { getCurrentLocale, localeCompareNumericLowercase } from '@ardoq/locale';
import Backbone from 'backbone';
import { BaseBackboneCollection } from 'BaseBackboneCollection';
import { DynamicPerspectiveFilter } from '@ardoq/perspectives';
import { globalFilters as Filters } from 'collections/filters';
import { groupingRuleOperations } from '@ardoq/core';
import { Perspective } from 'models/perspective';
import { activeFilter$ } from 'streams/filters/activeFilter$';
import { allWorkspacesClosed } from 'streams/views/mainContent/actions';
import { dispatchAction } from '@ardoq/rxbeach';
import { dynamicFilters } from 'filters/dynamicFilters';
import { getApiUrl } from 'backboneExtensions';
import { setActivePerspective } from 'streams/filters/FilterActions';
import { subscribeToAction } from 'streams/utils/streamUtils';
import { clearPerspectives } from './helpers/loadPerspective';
import { ActiveFilterState } from 'streams/filters/types';

const perspectiveIdQueryString = (perspectiveId: ArdoqId) =>
  `ps_@id=${perspectiveId}&`;

interface CreateAttributes {
  filters: PerspectiveFilters;
  groupBys: GroupingRule[];
  dynamicFilters: DynamicPerspectiveFilter[];
  name?: string | number | string[];
  workspace: string;
}
interface CreateOptions {
  setAsCurrent?: boolean;
  collection?: Perspectives;
}
interface CreateFromCurrentModifiersOptions {
  name?: string | number | string[];
  workspaceId: string;
  setAsCurrent: boolean;
  groupBys: GroupingRule[];
}

class Perspectives extends BaseBackboneCollection<PerspectiveBackboneModel>({
  comparator: (a: PerspectiveBackboneModel, b: PerspectiveBackboneModel) =>
    localeCompareNumericLowercase(
      a.get('name'),
      b.get('name'),
      // oxlint-disable-next-line ardoq-lint/no-get-current-locale -- Backbone Collection comparator; the engine owns the (a, b) signature, no channel to thread the Locale.
      getCurrentLocale()
    ),
  model: Perspective,
  url: `${getApiUrl(Backbone.Collection)}/api/perspective`,
}) {
  constructor() {
    super();

    subscribeToAction(allWorkspacesClosed, () => {
      clearPerspectives({ shouldTriggerChangeEvent: true });
    });
  }
  create(
    attributes: CreateAttributes,
    options: Backbone.ModelSaveOptions & CreateOptions = {}
  ) {
    options.collection = this;

    const perspective = new Perspective(attributes, options);
    const setAsCurrent = options.setAsCurrent;

    return perspective.save(undefined, {
      wait: true,
      success: () => {
        this.add(perspective);
        if (setAsCurrent) {
          dispatchAction(
            setActivePerspective({
              id: perspective.id,
              queryString: getModifiersAsQueryString(attributes.groupBys),
            })
          );
        }
      },
    });
  }

  createFromCurrentModifiers(options: CreateFromCurrentModifiersOptions) {
    return this.create(
      {
        filters: Filters.getPersistedFilters(),
        groupBys: options.groupBys,
        dynamicFilters: dynamicFilters.getDefinitions(),
        name: options.name,
        workspace: options.workspaceId,
      },
      { setAsCurrent: options.setAsCurrent }
    );
  }

  getCurrentSet() {
    const selectedPerspectiveId = activeFilter$.state.selectedPerspectiveId;
    if (selectedPerspectiveId === null) {
      return undefined;
    }
    return this.get(selectedPerspectiveId);
  }

  updateCurrentPerspective(groupBys: GroupingRule[]) {
    const currentSet = this.getCurrentSet();
    if (currentSet) {
      currentSet.set({
        filters: Filters.getPersistedFilters(),
        groupBys,
        dynamicFilters: dynamicFilters.getDefinitions(),
      });

      dispatchAction(
        setActivePerspective({
          id: currentSet.id,
          queryString: getModifiersAsQueryString(groupBys),
        })
      );
      return currentSet.save();
    }
    return Promise.resolve();
  }

  isCurrentSetModified(
    {
      activeFiltersQueryString,
      selectedPerspectiveQueryString,
      selectedPerspectiveId,
    }: ActiveFilterState,
    activeWorkspaceId: ArdoqId | null,
    groupBys: GroupingRule[]
  ) {
    if (
      !selectedPerspectiveId ||
      activeFiltersQueryString === selectedPerspectiveQueryString
    ) {
      return false;
    }
    if (
      selectedPerspectiveId &&
      activeFiltersQueryString ===
        perspectiveIdQueryString(selectedPerspectiveId)
    ) {
      const perspective = this.get(selectedPerspectiveId);
      if (!perspective) {
        return false;
      }

      const enrichedGroupBys = (
        (perspective.get('groupBys') as GroupingRule[]) ?? []
      ).map(rule =>
        groupingRuleOperations.enrichGroupingRuleWithWorkspaceId(
          rule,
          activeWorkspaceId ?? null
        )
      );
      return (
        getModifiersAsQueryString(groupBys) !==
        getPerspectiveModifiersAsQueryString(
          perspective.get('filters'),
          enrichedGroupBys
        )
      );
    }
    return true;
  }

  encodeURI(
    activeFilters: ActiveFilterState,
    activeWorkspaceId: ArdoqId | null,
    groupBys: GroupingRule[]
  ) {
    const currentSet = this.getCurrentSet();
    if (
      currentSet &&
      !this.isCurrentSetModified(activeFilters, activeWorkspaceId, groupBys)
    ) {
      return perspectiveIdQueryString(currentSet.id);
    }
    return getModifiersAsQueryString(groupBys);
  }
}

export const perspectives = new Perspectives();
